// apps/web/src/components/threat-actors/ThreatActorFilterWorkbench.tsx
//
// KAN-221 — the filter workbench. Preserves the existing SERVER-BACKED filters
// (attribution and supported sorts) with clearer labels and a reset action
// that appears only when a filter is non-default. Speculative client-only quick
// chips (Recently seen / Reference-rich / Unknown origin) were deliberately
// omitted: the list is capped at 50 server-side, so client-side chip counts
// would imply corpus-wide filtering they cannot honestly deliver.

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { RotateCcw } from 'lucide-react'
import {
  THREAT_ACTOR_SORT_OPTIONS,
  type ThreatActorSort,
} from '@/lib/types'
import { attributionLabel } from './threat-actor-utils'

const SORT_LABELS: Record<ThreatActorSort, string> = {
  alpha: 'Alphabetical (A→Z)',
  'linked-stories': 'Most linked stories',
}

const CAPTION = 'text-[9.5px] font-semibold uppercase tracking-[0.09em] text-muted-foreground'

export interface ThreatActorFilterWorkbenchProps {
  attribution: string
  sort: ThreatActorSort
  hasActiveFilters: boolean
  onAttribution: (v: string) => void
  onSort: (v: ThreatActorSort) => void
  onReset: () => void
}

export function ThreatActorFilterWorkbench(props: ThreatActorFilterWorkbenchProps) {
  return (
    <div
      data-testid="filter-rail"
      className="flex flex-wrap items-end gap-3 rounded-t-lg border border-border bg-card px-4 py-3"
    >
      <label className="flex flex-col gap-1">
        <span className={CAPTION}>Attribution</span>
        <Select value={props.attribution} onValueChange={props.onAttribution}>
          <SelectTrigger
            className="w-44"
            aria-label="Filter by attribution"
            data-testid="threat-actors-attribution-filter"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All classes</SelectItem>
            <SelectItem value="state">{attributionLabel('state')}</SelectItem>
            <SelectItem value="criminal">{attributionLabel('criminal')}</SelectItem>
            <SelectItem value="hacktivist">{attributionLabel('hacktivist')}</SelectItem>
            <SelectItem value="unknown">{attributionLabel('unknown')}</SelectItem>
          </SelectContent>
        </Select>
      </label>


      <label className="ml-auto flex flex-col gap-1">
        <span className={CAPTION}>Sort</span>
        <Select
          value={props.sort}
          onValueChange={(v) => props.onSort(v as ThreatActorSort)}
        >
          <SelectTrigger
            className="w-52"
            aria-label="Sort threat actors"
            data-testid="threat-actors-sort"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {THREAT_ACTOR_SORT_OPTIONS.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {SORT_LABELS[opt]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>

      {props.hasActiveFilters && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={props.onReset}
          className="h-9 self-end text-muted-foreground"
        >
          <RotateCcw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          Reset
        </Button>
      )}
    </div>
  )
}
